import React from 'react';

import styled from 'styled-components';

import { NORTH_AMERICAN_PHONE_PLACEHOLDER } from 'constants/phoneNumbers';
import {
  formatNorthAmericanPhoneInput,
  getNorthAmericanPhoneInputValue,
  validateOptionalNorthAmericanPhone,
} from 'helpers/phoneNumbers';

import Input from './Input';

type Props = {
  disabled?: boolean;
  onChange?: (value: string) => void;
  placeholder?: string;
  value?: string | null;
};

export const PHONE_INPUT_VALIDATION_RULES = [{ validator: validateOptionalNorthAmericanPhone }];

const Container = styled.div`
  position: relative;
`;

const MaskedInput = styled(Input)`
  color: transparent;
  caret-color: var(--text-primary);

  &::selection {
    color: transparent;
  }
`;

const Mask = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  padding: 4px 11px;
  pointer-events: none;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
`;

const StaticMaskText = styled.span`
  color: var(--text-secondary);
`;

const MaskAreaCodeSeparator = styled(StaticMaskText)`
  margin-right: 4px;
`;

const EnteredDigit = styled.span`
  display: inline-block;
  width: 1ch;
  text-align: center;
  color: var(--text-primary);
`;

const PlaceholderDigit = styled.span`
  display: inline-block;
  width: 1ch;
  text-align: center;
  color: var(--line-gray-strong);
`;

const PhoneMask = ({ value }: { value?: string | null }) => {
  const nationalNumber = getNorthAmericanPhoneInputValue(value).slice(2);
  const digits = Array.from({ length: 10 }, (_, index) => nationalNumber[index]);

  return (
    <Mask aria-hidden="true">
      <StaticMaskText>+1 (</StaticMaskText>
      <PhoneMaskDigits digits={digits} end={3} />
      <MaskAreaCodeSeparator>)</MaskAreaCodeSeparator>
      <PhoneMaskDigits digits={digits} end={6} start={3} />
      <StaticMaskText>-</StaticMaskText>
      <PhoneMaskDigits digits={digits} start={6} />
    </Mask>
  );
};

const PhoneMaskDigits = ({
  digits,
  end,
  start = 0,
}: {
  digits: (string | undefined)[];
  end?: number;
  start?: number;
}) => (
  <>
    {digits
      .slice(start, end)
      .map((digit, index) =>
        digit ? (
          <EnteredDigit key={index + start}>{digit}</EnteredDigit>
        ) : (
          <PlaceholderDigit key={index + start}>5</PlaceholderDigit>
        )
      )}
  </>
);

const PhoneInput = ({ disabled, onChange, placeholder = NORTH_AMERICAN_PHONE_PLACEHOLDER, value }: Props) => {
  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(getNorthAmericanPhoneInputValue(event.target.value));
    },
    [onChange]
  );

  return (
    <Container>
      <MaskedInput
        aria-label={placeholder}
        disabled={disabled}
        inputMode="tel"
        onChange={handleChange}
        placeholder=""
        value={formatNorthAmericanPhoneInput(value)}
      />
      <PhoneMask value={value} />
    </Container>
  );
};

export default PhoneInput;
